import React, { useState, useEffect, useCallback } from 'react';

/**
 * ReturnToTop Component
 * 
 * A high-performance, fully customizable scroll-to-top floating button.
 * Supports multiple icon shapes, text labels, smooth scrolling, entrance animations,
 * custom CSS styling, glassmorphism, hover transitions, and responsive visibility.
 */

// Helper to normalize CSS measurement units
const addUnit = (val, defaultUnit = 'px') => {
    if (!val && val !== 0) return undefined;
    const str = String(val).trim();
    if (/^-?\d+(\.\d+)?$/.test(str)) return `${str}${defaultUnit}`;
    return str;
};

// Built-in crisp SVG Icons
export const ReturnToTopIcons = {
    'chevron-up': (size = '20', color = 'currentColor') => (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="18 15 12 9 6 15" />
        </svg>
    ),
    'arrow-up': (size = '20', color = 'currentColor') => (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="19" x2="12" y2="5" />
            <polyline points="5 12 12 5 19 12" />
        </svg>
    ),
    'arrow-circle-up': (size = '20', color = 'currentColor') => (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <polyline points="16 12 12 8 8 12" />
            <line x1="12" y1="16" x2="12" y2="8" />
        </svg>
    ),
    'double-up': (size = '20', color = 'currentColor') => (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="17 11 12 6 7 11" />
            <polyline points="17 18 12 13 7 18" />
        </svg>
    ),
    'triangle-up': (size = '20', color = 'currentColor') => (
        <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
            <polygon points="12,4 22,20 2,20" />
        </svg>
    ),
    'long-arrow-up': (size = '20', color = 'currentColor') => (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="21" x2="12" y2="3" />
            <polyline points="6 9 12 3 18 9" />
        </svg>
    )
};

export const ReturnToTop = ({
    settings = {},
    forceVisible = false, // useful for live designer previews
    containerRef = null,
    className = '',
    style = {},
    onClick = null
}) => {
    // Customization Settings with robust defaults
    const isEnabled = settings.returnToTopEnable !== false && settings.returnToTopEnable !== 'false';
    const threshold = parseInt(settings.returnToTopThreshold, 10) || 280;
    const position = settings.returnToTopPosition || 'bottom-right';
    const offsetX = addUnit(settings.returnToTopOffsetX || '24px');
    const offsetY = addUnit(settings.returnToTopOffsetY || '24px');
    const zIndex = parseInt(settings.returnToTopZIndex, 10) || 9999;

    const type = settings.returnToTopType || 'icon-only'; // 'icon-only' | 'icon-text' | 'text-icon' | 'text-only'
    const text = settings.returnToTopText || 'Top';
    const iconName = settings.returnToTopIcon || 'chevron-up';
    const iconSize = addUnit(settings.returnToTopIconSize || '20px');
    const shape = settings.returnToTopShape || 'circle'; // 'circle' | 'rounded' | 'square' | 'pill'

    const primaryColor = settings.primaryColor || '#3b82f6';
    const bgColor = settings.returnToTopBgColor || primaryColor;
    const textColor = settings.returnToTopTextColor || '#ffffff';
    const border = settings.returnToTopBorder || 'none';
    const shadow = settings.returnToTopShadow || '0 4px 14px rgba(0, 0, 0, 0.25)';
    const backdropBlur = addUnit(settings.returnToTopBackdropBlur || '0px');
    const opacity = settings.returnToTopOpacity || '1';

    const hoverBg = settings.returnToTopHoverBg || (bgColor === primaryColor ? '#2563eb' : '#1e293b');
    const hoverColor = settings.returnToTopHoverColor || textColor;
    const hoverBorder = settings.returnToTopHoverBorder || border;
    const hoverShadow = settings.returnToTopHoverShadow || '0 8px 24px rgba(0, 0, 0, 0.35)';
    const hoverTransform = settings.returnToTopHoverTransform || 'translateY(-4px) scale(1.05)';

    const fontSize = addUnit(settings.returnToTopFontSize || '0.85rem');
    const fontWeight = settings.returnToTopFontWeight || '600';
    const fontFamily = (settings.returnToTopFontFamily && settings.returnToTopFontFamily !== 'inherit') 
        ? settings.returnToTopFontFamily 
        : undefined;

    const customWidth = settings.returnToTopWidth ? addUnit(settings.returnToTopWidth) : undefined;
    const customHeight = settings.returnToTopHeight ? addUnit(settings.returnToTopHeight) : undefined;
    const customPadding = settings.returnToTopPadding ? addUnit(settings.returnToTopPadding) : undefined;
    const customRadius = settings.returnToTopRadius ? addUnit(settings.returnToTopRadius) : undefined;

    const animation = settings.returnToTopAnimation || 'slide-up'; // 'fade' | 'slide-up' | 'zoom' | 'bounce' | 'none'
    const transition = settings.returnToTopTransition || 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
    const scrollBehavior = settings.returnToTopScrollBehavior || 'smooth';

    const hideMobile = settings.returnToTopHideMobile === true || settings.returnToTopHideMobile === 'true';
    const hideDesktop = settings.returnToTopHideDesktop === true || settings.returnToTopHideDesktop === 'true';

    const [visible, setVisible] = useState(forceVisible);
    const [isHovered, setIsHovered] = useState(false);
    const [isPressed, setIsPressed] = useState(false);

    // Scroll listener with RAF throttling to prevent forced reflows
    useEffect(() => {
        if (forceVisible) {
            setVisible(true);
            return;
        }

        let ticking = false;
        const handleScroll = () => {
            if (!ticking) {
                window.requestAnimationFrame(() => {
                    const currentScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
                    setVisible(currentScrollY > threshold);
                    ticking = false;
                });
                ticking = true;
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll(); // Initial check

        return () => window.removeEventListener('scroll', handleScroll);
    }, [threshold, forceVisible]);

    const scrollToTop = useCallback((e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        if (onClick) {
            onClick(e);
            return;
        }
        window.scrollTo({
            top: 0,
            behavior: scrollBehavior
        });
    }, [onClick, scrollBehavior]);

    if (!isEnabled && !forceVisible) {
        return null;
    }

    // Default dimensions by shape
    let defaultWidth = '46px';
    let defaultHeight = '46px';
    let defaultRadius = '50%';
    let defaultPadding = '0px';

    if (shape === 'square') {
        defaultRadius = '0px';
    } else if (shape === 'rounded') {
        defaultRadius = '10px';
    } else if (shape === 'pill') {
        defaultWidth = 'auto';
        defaultHeight = '42px';
        defaultRadius = '9999px';
        defaultPadding = '8px 18px';
    }

    // Position coordinates
    const positionStyle = {};
    if (forceVisible) {
        positionStyle.position = 'relative';
        positionStyle.bottom = 'auto';
        positionStyle.right = 'auto';
        positionStyle.left = 'auto';
        positionStyle.top = 'auto';
    } else {
        positionStyle.position = 'fixed';
        positionStyle.zIndex = zIndex;

        if (position === 'bottom-right') {
            positionStyle.bottom = offsetY;
            positionStyle.right = offsetX;
        } else if (position === 'bottom-left') {
            positionStyle.bottom = offsetY;
            positionStyle.left = offsetX;
        } else if (position === 'bottom-center') {
            positionStyle.bottom = offsetY;
            positionStyle.left = '50%';
            positionStyle.transform = isHovered 
                ? `translateX(-50%) ${hoverTransform}` 
                : 'translateX(-50%)';
        } else if (position === 'top-right') {
            positionStyle.top = offsetY;
            positionStyle.right = offsetX;
        } else if (position === 'top-left') {
            positionStyle.top = offsetY;
            positionStyle.left = offsetX;
        }
    }

    // Animation / Entrance Visibility
    const isShowing = visible || forceVisible;
    let opacityValue = isShowing ? (parseFloat(opacity) || 1) : 0;
    let transformValue = 'none';

    if (position === 'bottom-center' && !forceVisible) {
        transformValue = isShowing 
            ? (isHovered ? `translateX(-50%) ${hoverTransform}` : 'translateX(-50%)')
            : 'translateX(-50%) translateY(20px)';
    } else {
        if (!isShowing) {
            if (animation === 'slide-up') transformValue = 'translateY(24px)';
            else if (animation === 'zoom') transformValue = 'scale(0.5)';
            else if (animation === 'bounce') transformValue = 'translateY(30px)';
            else transformValue = 'none';
        } else {
            if (isPressed) transformValue = 'scale(0.95)';
            else if (isHovered) transformValue = hoverTransform;
            else transformValue = 'none';
        }
    }

    // Render Icon
    const iconRenderer = ReturnToTopIcons[iconName] || ReturnToTopIcons['chevron-up'];
    const currentIconColor = isHovered ? hoverColor : textColor;
    const renderedIcon = iconRenderer(iconSize, currentIconColor);

    return (
        <>
            {/* Responsive Media Query Styles */}
            <style dangerouslySetInnerHTML={{
                __html: `
                    @media (max-width: 768px) {
                        .omnivaso-return-to-top {
                            ${hideMobile ? 'display: none !important;' : ''}
                        }
                    }
                    @media (min-width: 769px) {
                        .omnivaso-return-to-top {
                            ${hideDesktop ? 'display: none !important;' : ''}
                        }
                    }
                `
            }} />

            <button
                type="button"
                className={`omnivaso-return-to-top ${className}`}
                onClick={scrollToTop}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => { setIsHovered(false); setIsPressed(false); }}
                onMouseDown={() => setIsPressed(true)}
                onMouseUp={() => setIsPressed(false)}
                aria-label={settings.returnToTopAriaLabel || 'Return to top of page'}
                title={settings.returnToTopTooltip || 'Return to Top'}
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    width: customWidth || (type === 'icon-only' ? defaultWidth : (shape === 'pill' ? 'auto' : defaultWidth)),
                    height: customHeight || defaultHeight,
                    minWidth: (type !== 'icon-only') ? '44px' : undefined,
                    padding: customPadding || defaultPadding,
                    borderRadius: customRadius || defaultRadius,
                    backgroundColor: isHovered ? hoverBg : bgColor,
                    color: isHovered ? hoverColor : textColor,
                    border: isHovered ? hoverBorder : border,
                    boxShadow: isHovered ? hoverShadow : shadow,
                    backdropFilter: backdropBlur !== '0px' ? `blur(${backdropBlur})` : 'none',
                    WebkitBackdropFilter: backdropBlur !== '0px' ? `blur(${backdropBlur})` : 'none',
                    opacity: opacityValue,
                    visibility: isShowing ? 'visible' : 'hidden',
                    pointerEvents: isShowing ? 'auto' : 'none',
                    cursor: 'pointer',
                    outline: 'none',
                    boxSizing: 'border-box',
                    userSelect: 'none',
                    transition: transition,
                    transform: transformValue,
                    fontSize: fontSize,
                    fontWeight: fontWeight,
                    fontFamily: fontFamily,
                    lineHeight: 1,
                    textDecoration: 'none',
                    ...positionStyle,
                    ...style
                }}
            >
                {/* Content: Icon / Text combinations */}
                {(type === 'icon-only' || type === 'icon-text') && renderedIcon}
                {(type === 'text-only' || type === 'icon-text' || type === 'text-icon') && (
                    <span style={{ display: 'inline-block', lineHeight: 1, whiteSpace: 'nowrap' }}>
                        {text}
                    </span>
                )}
                {type === 'text-icon' && renderedIcon}
            </button>
        </>
    );
};

export default ReturnToTop;
